import { Component, Injector, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuComponent } from "../../components/menu/menu.component";
import {
  ApiService,
  CommonFunctionsService,
  DatastoreService,
  EXTENSION_DESCRIPTION_30,
  EXTENSION_DESCRIPTION_60,
  ROOM_HIRE_DESCRIPTION,
  attributePaidAcrossLinks,
  depositAttributed,
  lineOutstanding,
  linksOutstanding,
  round2,
  PaymentStartService,
} from '../../services';
import { NgxSpinnerService } from "ngx-spinner";
import moment from 'moment-timezone';
import { PaymentServiceTokens } from '../../app.module';
import { IPayment } from '../../interfaces/ipayment';

@Component({
  selector: 'app-payment-information',
  templateUrl: './payment-information.component.html',
  styleUrls: ['./payment-information.component.scss']
})
export class PaymentInformationComponent implements OnInit {

  config: any;
  site: string = "";
  companyDetails: any;
  booking: any;
  company: any;
  service: any;
  servicePriceDisplay: string;
  servicePrice: number;
  totalPrice: number;
  totalPriceDisplay: string | [string, string];
  purchase: any;
  purchaseHistory: any = [];
  purchaseHistoryTotal: number = 0;
  purchaseHistoryTotalDisplay: string | [string, string];
  ceremonyFees: any[] = [];
  serviceExtraItems: any[] = [];
  showInstallments: boolean = false;
  showBalance: boolean = false;
  showExtraItems: boolean = false;
  paying: boolean = false;
  paymentProvider: string = '';
  paymentAction: string = '';
  totalOutstanding: number = 0;
  totalOutstandingDisplay: string = 'calculating...';
  showClose: boolean = true;
  priceMinusPaidDisplay: string;
  paymentResult: '' | 'success' | 'failed' = '';
  revCodeError: boolean = false;
  /** The payment_started stamp failed — the payment was not started. */
  paymentStartError = false;
  bespokePlus: boolean = false;
  bespokeExtra: boolean = false;
  /**
   * What the customer still owes across the booking's extra-item links, in
   * pounds: Σ max(cost×qty − paid, 0) over reconciledFees.
   */
  balanceOutstanding: number = 0;
  // > 0 when the payments made total more than the fee-line totals — e.g.
  // after a reschedule to a cheaper venue. Drives the refund-due banner.
  refundDue: number = 0;
  refundDueDisplay: string = '';
  /**
   * The booking's links with `data.paid` reconciled. RWRSPI-1026 bookings
   * carry real stamps (deposit on the booking-fee line, 0 elsewhere) and
   * pass through unchanged; legacy bookings (lines never stamped at booking
   * time) get the purchase-payments history total attributed across
   * in-memory copies so the deposit and any historic instalments are never
   * re-charged. This is what payBalance charges from and what
   * payment-amount receives.
   */
  reconciledFees: any[] = [];
  /** True when the deposit was never attributed per line (pre-RWRSPI-1026 booking). */
  private legacyPaidModel: boolean = true;


  constructor(
    private route: ActivatedRoute,
    private dataStore: DatastoreService,
    private commonFunctionsService: CommonFunctionsService,
    private apiService: ApiService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private injector: Injector,
    private paymentStart: PaymentStartService
  ) { }

  get communicationLink(): any[] {
    return [
      '/',
      this.commonFunctionsService.getCompanyName(),
      this.commonFunctionsService.getCompanyId(),
      'communication',
    ];
  }

  private scrollToPaymentStartError(): void {
    setTimeout(() => {
      document
        .querySelector('[data-test="payment-start-error"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  /**
   * Full page load, not a router navigation: the likeliest stamp-failure cause
   * is an expired session, and only a document load re-runs the auth flow and
   * gets a fresh token.
   */
  restart(): void {
    window.location.assign(
      `/${this.commonFunctionsService.getCompanyName()}/${this.commonFunctionsService.getCompanyId()}/our-ceremony`,
    );
  }

  private scrollToRevCodeError(): void {
    setTimeout(() => {
      document
        .querySelector('[data-test="payment-rev-code-error"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  ngOnInit(): void {
    this.route.params.subscribe(async params => {
      await this.commonFunctionsService.setCompanyName(params);
      this.site = params.site;
    });

    this.route.queryParams.subscribe((qp) => {
      if (qp.payment === 'success' || qp.payment === 'failed') {
        this.paymentResult = qp.payment;
      }
    });

    this.company = this.dataStore.selectedCompany;
    if (!this.company) {
      const companyStr = localStorage.getItem('company') as string;
      this.company = JSON.parse(companyStr);
    }

    this.service = this.dataStore.selectedService;
    if (!this.service) {
      const serviceStr = localStorage.getItem('service') as string;
      this.service = JSON.parse(serviceStr);
    }
    console.log(this.service);

    // get purchase
    this.setup();

  }

  async setup() {

    const configStr = this.dataStore.config;
    const config = JSON.parse(configStr);
    console.log(config);
    if (config.hasOwnProperty('myCeremony')) {
      if (config.myCeremony.hasOwnProperty('payment-information')) {
        if (config.myCeremony['payment-information'].hasOwnProperty('paymentProvider')) {
          this.paymentProvider = config.myCeremony['payment-information'].paymentProvider;
        }
        if (config.myCeremony['payment-information'].hasOwnProperty('make-payment-options')) {
          if (config.myCeremony['payment-information']['make-payment-options'].installments) {
            this.showInstallments = true;
          }

          if (config.myCeremony['payment-information']['make-payment-options'].balance) {
            this.showBalance = true;
          }

          if (config.myCeremony['payment-information']['make-payment-options'].extra_items) {
            this.showExtraItems = true;
          }

        }
      }
    }

    this.booking = this.dataStore.booking;
    if (!this.booking) {
      const bookingStr = localStorage.getItem('ceremony') as string;
      this.booking = JSON.parse(bookingStr);
    }

    this.purchase = await this.apiService.getPurchase(this.booking.company_id, this.service.id, this.booking.purchase_id);
    console.log(this.purchase);

    let GBP = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
    });
    this.servicePriceDisplay = GBP.format(this.purchase.price / 100);
    this.servicePrice = this.purchase.price / 100;

    this.totalPrice = this.servicePrice;
    this.totalPriceDisplay = GBP.format(this.totalPrice);

    this.priceMinusPaidDisplay = GBP.format((this.purchase.price - this.purchase.paid)/100);

    // get extra items
    try {
      let itemExtraTotal = 0;
      const links = await this.apiService.getServiceItemExtraLinksForBooking(this.booking.company_id, this.booking.id) as any;
      console.log(links);
      for (const l of links) {
        let p = new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP'
        });
        const lineCost = l.data.cost * (l.data.quantity ? parseInt(l.data.quantity) : 1);
        l.price =  this.splitCost(p.format(lineCost));
        // £0 lines (the bespoke/extension markers) are part of the package —
        // shown as "Included" rather than £0.00 so they don't read as free.
        l.included = lineCost === 0;

        itemExtraTotal += lineCost;
      }
      this.ceremonyFees = links;
      this.totalPrice = itemExtraTotal;
      this.totalPriceDisplay = this.splitCost(GBP.format(this.totalPrice));
      this.totalOutstanding = this.totalPrice;

      // get all extra items (including hidden) to retrieve pmtTyp revenue codes
      try {
        this.serviceExtraItems = await this.apiService.getServiceItemExtras(
          this.booking.company_id,
          this.service.global_id,
          null,
          true
        ).toPromise();
        console.log('serviceExtraItems', this.serviceExtraItems);

        // match pmtTyp from extra items onto the linked fees by item_id
        let roomHireLink: any = null;
        for (const link of this.ceremonyFees) {
          const match = this.serviceExtraItems.find(ei => ei.item_id === link.item_id);
          console.log('match, ', match);
          if (match?.data?.pmtTyp) {
            link.pmtTyp = match.data.pmtTyp;
          }
          if (match?.item_description === EXTENSION_DESCRIPTION_30) {
            this.bespokePlus = true;
          } else if (match?.item_description === EXTENSION_DESCRIPTION_60) {
            this.bespokeExtra = true;
          } else if (match?.item_description === ROOM_HIRE_DESCRIPTION) {
            roomHireLink = link;
          }
          console.log(`Fee: ${link.data?.item} | item_id: ${link.item_id} | pmtTyp: ${link.pmtTyp || 'not found'}`);
        }

        // Display-only: data.item is left untouched as it feeds the
        // payment-gateway fee lines.
        if (roomHireLink && (this.bespokePlus || this.bespokeExtra)) {
          roomHireLink.displayItem = `${roomHireLink.data?.item} (${this.bespokePlus ? 'Bespoke Plus' : 'Bespoke Extra'})`;
        }
      }
      catch (err) {
        console.error('Failed to fetch service extra items', err);
      }

    }
    catch (err) {
      console.error(err);
    }

    // get purchase history
    const purchaseHistory = await this.apiService.getPurchasePayments(this.booking.company_id, this.service.id, this.booking.purchase_id) as any;
    if (purchaseHistory._embedded?.payments && purchaseHistory._embedded.payments.length > 0) {
      this.purchaseHistoryTotal = 0;
      this.purchaseHistory = [];
      for (const purchase of purchaseHistory._embedded.payments) {
        this.purchaseHistoryTotal += purchase.amount;
        let p = new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: purchase.currency
        });

        const obj = {
          notes: purchase.notes,
          ref: purchase.reference,
          currency: purchase.currency,
          amount: this.splitCost(p.format(purchase.amount)),
          original_amount: purchase.amount,
          status: purchase.payment_status,
          type: purchase.payment_type,
          date: moment(purchase.created_at).utc().tz('Europe/London').format('DD/MM/YYYY')
        };
        this.purchaseHistory.push(obj);
        this.totalOutstanding -= purchase.amount;
      }

      let pht = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: purchaseHistory._embedded.payments[0].currency
      });
      this.purchaseHistoryTotalDisplay = this.splitCost(pht.format(this.purchaseHistoryTotal));
      this.totalOutstandingDisplay = pht.format(this.totalOutstanding);
    }
    console.log(this.purchaseHistory);

    // Reconcile per-line paid state. RWRSPI-1026 bookings carry real stamps
    // (deposit attributed to the booking-fee line) and pass through as-is.
    // Legacy bookings never had their payments attributed per line, so the
    // purchase-payments history total — the reliable record on Westminster,
    // purchase.paid is never rolled up — is attributed across in-memory
    // copies before anything is charged. Settlement after the next balance
    // payment stamps the real links, migrating the booking permanently.
    this.legacyPaidModel = !depositAttributed(this.ceremonyFees);
    this.reconciledFees = this.legacyPaidModel
      ? attributePaidAcrossLinks(this.ceremonyFees, this.purchaseHistoryTotal)
      : this.ceremonyFees;
    this.balanceOutstanding = linksOutstanding(this.reconciledFees);
    this.priceMinusPaidDisplay = GBP.format(this.balanceOutstanding);

    // Overpayment check: lineOutstanding floors every line at zero, so an
    // overpaid booking shows balanceOutstanding 0 rather than negative — the
    // refund owed is the payments total beyond the fee-line totals. Skipped
    // when no fee lines loaded, as every payment would then read as
    // refundable.
    if (this.ceremonyFees.length > 0) {
      this.refundDue = round2(this.purchaseHistoryTotal - this.totalPrice);
      if (this.refundDue > 0) {
        this.refundDueDisplay = GBP.format(this.refundDue);
      }
    }
    console.log(
      'Balance outstanding', this.balanceOutstanding,
      'legacy paid model?', this.legacyPaidModel,
      'payments made', this.purchaseHistoryTotal,
    );
    

  }

  splitCost(input: string): [string, string] {
    if (input.length === 0) {
      return ["", ""];
    }
    const currencySymbol = input.charAt(0);
    const price = input.slice(1);
    console.log("split", [currencySymbol, price]);
    return [currencySymbol, price]; 
  }

  async pay(action: string) {
    this.revCodeError = false;
    if (action === 'balance') {
      await this.payBalance();
      return;
    }
    this.paymentAction = action;
    this.paying = true;
  }

  private async payBalance() {
    localStorage.setItem('paymentAction', 'balance');
    this.revCodeError = false;

    const tokenKey = this.paymentProvider || 'capita';
    const token = PaymentServiceTokens.get(tokenKey);
    const paymentService = this.injector.get(token) as IPayment;

    let service = this.dataStore.selectedService;
    if (!service) {
      service = JSON.parse(localStorage.getItem('service') as string);
    }

    let extraItem: any = false;
    if (service?.extra?.fees_extra_item_id) {
      try {
        extraItem = await this.apiService.getServiceItemExtra(
          this.booking.company_id,
          service.extra.fees_extra_item_id,
          service.global_id
        );
      } catch (err) {
        console.error('Failed to fetch fees extra item', err);
      }
    }

    // Per-line model: charge only each line's unpaid remainder, computed
    // from the reconciled links (legacy bookings carry the payment-history
    // attribution in-memory, so deposits/instalments are never re-charged).
    const feeItems = this.reconciledFees.filter(l => lineOutstanding(l.data) > 0);
    const outstandingPence = Math.round(this.balanceOutstanding * 100);

    if (feeItems.length === 0 || outstandingPence <= 0) {
      console.error('Blocking payment: nothing outstanding across the booking links', this.reconciledFees);
      this.revCodeError = true;
      this.scrollToRevCodeError();
      return;
    }

    if (paymentService.paymentType === 'heycentric' && feeItems.some(f => !f.pmtTyp)) {
      console.error('Blocking payment: one or more fee items have a null Rev code', feeItems);
      this.revCodeError = true;
      this.scrollToRevCodeError();
      return;
    }

    // Persist every link (not just the charged ones) so payment-verify can
    // stamp each one's data.paid = its line total after the webhook confirms
    // — a successful balance payment settles the whole booking, and on a
    // legacy booking this backfills the attributed deposit/instalment lines
    // so it moves onto the per-line model permanently.
    localStorage.setItem('paidLinks', JSON.stringify(this.reconciledFees));

    try {
      const init: any = await paymentService.invoke(
        this.booking.company_id,
        this.booking.id,
        this.booking.purchase_id,
        this.dataStore.member.id,
        extraItem?.item_description,
        extraItem?.data?.pmtTyp,
        null,
        outstandingPence,
        false,
        feeItems
      );

      const needsSCPCheck = paymentService.paymentType === 'capita';
      const hasSCP = needsSCPCheck ? !!init?.scp : true;

      if (init?.url && hasSCP) {
        if (needsSCPCheck) {
          localStorage.setItem('paymentId', init.scp);
        }
        // A failed stamp must ABORT before the gateway: verify treats a
        // missing stamp as a failed payment (see booking 11271).
        const stamped = await this.paymentStart.stampPaymentStarted(this.booking);
        if (!stamped) {
          localStorage.removeItem('paidLinks');
          localStorage.removeItem('paymentAction');
          this.paymentStartError = true;
          this.scrollToPaymentStartError();
          return;
        }
        this.booking = stamped;
        window.location.href = init.url;
      } else {
        console.error('NO URL RETURNED FROM PAYMENT PROVIDER', `Provider: ${paymentService.paymentType}`);
      }
    } catch (err) {
      console.error('Problem invoking payment method');
      console.error(err);
    }
  }


  closePaying() {
    this.paying = false;
  }

}
